import { Space, Tag } from 'antd';
import React, { useCallback, useEffect } from 'react';
import { Equipment } from 'domains/eqptDomain';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useTranslation } from 'react-i18next';
import { useMount } from 'ahooks';
import { RevxButton } from 'components/RevxButton';

const EqptAdminTable = () => {
  const { t } = useTranslation();
  // not show timeframe link for now
  // const match = useRouteMatch();

  const dispatch = useAppDispatch();

  // load components list for form use
  useMount(() => {
    dispatch.eqpt.getEqptTypes();
  });

  // table data processing
  const data = useAppSelector((state) => state?.eqpt?.data?.content || []);
  const total = useAppSelector((state) => state.eqpt.data?.totalElements || 0);
  const currentPage = useAppSelector((state) => state.eqpt.currentPage);
  const pageSize = useAppSelector((state) => state.eqpt.pageSize);
  const loading = useAppSelector((state) => state.loading.effects.eqpt.getList);
  const curItem = useAppSelector((state) => state.eqpt.item);
  const filterObj = useAppSelector((state) => state.eqpt.filterObj);

  useEffect(() => {
    dispatch.eqpt.getList({
      page: { currentPage, pageSize },
      query: { ...filterObj },
    });
  }, [currentPage, pageSize, dispatch.eqpt, filterObj]);

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          const currentPage = pagination.current;
          const pageSize = pagination.pageSize;
          dispatch.eqpt.setState({ currentPage, pageSize });
          break;
      }
    },
    [dispatch.eqpt]
  );
  const columns: TableColumnsType<Equipment> = [
    {
      title: t('common.id'),
      dataIndex: 'id',
    },
    {
      title: t('common.revxId'),
      dataIndex: 'revxId',
    },
    {
      title: t('platform.eqpt.table.columns.manufacturer'),
      dataIndex: 'manufacturer',
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
    },
    {
      title: t('platform.eqpt.table.columns.markupPct'),
      dataIndex: 'markupPct',
      valueType: 'percentage',
      responsive: ['xl'],
    },
    {
      title: t('platform.eqpt.table.columns.printUnits'),
      dataIndex: 'printUnits',
    },
    {
      title: t('platform.eqpt.table.columns.hourlyIncrement'),
      dataIndex: 'hourlyIncrement',
      valueType: 'money',
      responsive: ['xl'],
    },
    {
      title: t('platform.eqpt.table.columns.makereadyCost'),
      dataIndex: 'makereadyCostUsd',
      valueType: 'money',
      responsive: ['xl'],
    },
    {
      title: t('platform.eqpt.table.columns.plateUnitCost'),
      dataIndex: 'plateUnitCostUsd',
      valueType: 'money',
      responsive: ['xl'],
    },
    {
      title: t('platform.eqpt.table.columns.runSpeed'),
      responsive: ['xxl'],
      children: [
        {
          title: t('common.smallAbbrev'),
          dataIndex: 'avgRunSpeedSmall',
          align: 'center',
        },
        {
          title: t('common.largeAbbrev'),
          dataIndex: 'avgRunSpeedLarge',
          align: 'center',
        },
      ],
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'createAt',
      responsive: ['xxl'],
      valueType: 'time',
    },
    {
      title: t('common.active'),
      dataIndex: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            key="view"
            size="small"
            onClick={() => dispatch.eqpt.setState({ item: record })}
          >
            {t('common.view')}
          </RevxButton>
          {/* 
            <RevxButton
            type="link"
            key="tf"
            onClick={() =>
              history.push(`${match.path}/${record.id}/tf`)
            }
          >
            {t('platform.platformMenu.tf')}
          </RevxButton>
          */}
        </Space>
      ),
    },
  ];

  return (
    <div hidden={!!curItem}>
      <RevxTable<Equipment>
        size="small"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          pageSize,
          current: currentPage,
          total,
        }}
        rowClassName={(record) => {
          if (curItem?.id === record.id) {
            return 'editing';
          }
          return '';
        }}
        onChange={onTableChange}
      />
    </div>
  );
};

export default EqptAdminTable;
